import {
  accountColumnLabel,
  accountColumnLabelLines,
} from '@components/AccountAllocationGrid/accountColumnLabel';
import type { AccountAllocationGridReport } from '@components/AccountAllocationGrid/types';
import type { TranslatedString } from '@interfaces/shared';
import type { ApplyBlockReason } from '@ovComponents/4-module/workflowCompletion/subSteps/editAccountAllocations/applyAccountAllocationsChecks';
import { WorkflowContext } from '@ovComponents/4-module/workflowCompletion/workflowCompletionContext';
import { Box, Button, CircularProgress, Typography } from 'hephaestus/web';
import { type ReactNode, useContext } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocalization } from '../../../../../hooks/useLocalization';
import { useApplyAccountAllocations } from './useApplyAccountAllocations';

type ApplyAccountAllocationsOptions = {
  title?: TranslatedString;
};

export type ApplyAccountAllocationsProp = {
  options: ApplyAccountAllocationsOptions;
  clientGroupId?: string;
  onNext: (branchId?: string) => Promise<unknown> | void;
};

const BLOCKED_MESSAGE_KEYS = {
  MISSING_SNAPSHOT: 'applyAccountAllocations.blocked.MISSING_SNAPSHOT',
  STALE_REPORT: 'applyAccountAllocations.blocked.STALE_REPORT',
  MEMBERSHIP_CHANGED: 'applyAccountAllocations.blocked.MEMBERSHIP_CHANGED',
  BASELINE_CHANGED: 'applyAccountAllocations.blocked.BASELINE_CHANGED',
} as const satisfies Record<ApplyBlockReason['code'], string>;

// The backend rejected the signed values themselves. Retry would send the same
// matrix again, so only a new allocation edit can get past these.
const FINAL_ERROR_CODES = new Set([
  'PRODUCT_NOT_HELD',
  'CAPACITY_BREACH',
  'WEIGHT_SUM_OUT_OF_RANGE',
  'AMOUNT_SUM_OUT_OF_RANGE',
  'AGGREGATE_CONSERVATION',
  'BAD_USER_INPUT',
]);

// The Portfolio tab's copy for these codes describes what that tab does next,
// which this step doesn't.
const STEP_ERROR_MESSAGE_KEYS = new Map([
  ['NOT_ACCESSIBLE', 'applyAccountAllocations.errors.NOT_ACCESSIBLE'],
  ['NOT_CUSTOMIZED', 'applyAccountAllocations.errors.NOT_CUSTOMIZED'],
  ['WRONG_REPORT_MEMBER', 'applyAccountAllocations.errors.WRONG_REPORT_MEMBER'],
]);

const errorMessageKey = (code: string) =>
  STEP_ERROR_MESSAGE_KEYS.get(code) ??
  `components:householdPortfolio.errors.${code}`;

const StepStatus = ({
  title,
  children,
}: {
  title: string;
  children: ReactNode;
}) => (
  <Box
    sx={{
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      gap: 2,
      minHeight: '300px',
      textAlign: 'center',
    }}
  >
    <Typography variant='headingMedium'>{title}</Typography>
    {children}
  </Box>
);

const AccountList = ({
  heading,
  accounts,
}: {
  heading: string;
  accounts: { subAccountId: string; label: string }[];
}) => (
  <Box>
    <Typography variant='bodyMedium' sx={{ fontWeight: 500 }}>
      {heading}
    </Typography>
    <Box
      component='ul'
      aria-label={heading}
      sx={{ m: 0, p: 0, listStyle: 'none' }}
    >
      {accounts.map(({ subAccountId, label }) => (
        <Typography key={subAccountId} component='li' variant='bodyMedium'>
          {label}
        </Typography>
      ))}
    </Box>
  </Box>
);

const MembershipChanges = ({
  added,
  removed,
  report,
}: {
  added: string[];
  removed: string[];
  report: AccountAllocationGridReport | null;
}) => {
  const { t } = useTranslation(['workflowCompletions', 'accountTypes']);

  // A removed account has usually left the report too, so it may have no label.
  const labelled = (subAccountIds: string[]) =>
    subAccountIds.map((subAccountId) => {
      const account = report?.subAccounts.find(
        (candidate) => candidate.subAccount.id === subAccountId,
      );
      return {
        subAccountId,
        label: account
          ? accountColumnLabelLines(
              accountColumnLabel(account.subAccount.account, t),
            ).join(' ')
          : t('applyAccountAllocations.accountNoLongerInHousehold'),
      };
    });

  return (
    <>
      {added.length > 0 && (
        <AccountList
          heading={t('applyAccountAllocations.accountsAdded')}
          accounts={labelled(added)}
        />
      )}
      {removed.length > 0 && (
        <AccountList
          heading={t('applyAccountAllocations.accountsRemoved')}
          accounts={labelled(removed)}
        />
      )}
    </>
  );
};

export const ApplyAccountAllocations = ({
  options,
  clientGroupId,
  onNext,
}: ApplyAccountAllocationsProp) => {
  const { translateBackendString } = useLocalization();
  const { workflowData } = useContext(WorkflowContext);
  const { t } = useTranslation(['workflowCompletions', 'components']);

  const householdClientGroupId =
    workflowData.currentHouseholdClientGroupId ?? clientGroupId;

  const { state, report, retry } = useApplyAccountAllocations({
    householdClientGroupId,
    onNext,
  });

  const title =
    translateBackendString(options?.title) ||
    t('applyAccountAllocations.title');

  if (state.status === 'BLOCKED') {
    const { reason } = state;
    return (
      <StepStatus title={title}>
        <Typography variant='bodyMedium'>
          {t(BLOCKED_MESSAGE_KEYS[reason.code])}
        </Typography>
        {reason.code === 'MEMBERSHIP_CHANGED' && (
          <MembershipChanges
            added={reason.added}
            removed={reason.removed}
            report={report}
          />
        )}
        <Typography variant='bodyMedium'>
          {t('applyAccountAllocations.startAgain')}
        </Typography>
      </StepStatus>
    );
  }

  if (state.status === 'FAILED') {
    return (
      <StepStatus title={title}>
        <Typography variant='bodyMedium'>
          {t(errorMessageKey(state.code))}
        </Typography>
        {FINAL_ERROR_CODES.has(state.code) ? (
          <Typography variant='bodyMedium'>
            {t('applyAccountAllocations.startAgain')}
          </Typography>
        ) : (
          <Button onClick={retry}>{t('applyAccountAllocations.retry')}</Button>
        )}
      </StepStatus>
    );
  }

  return (
    <StepStatus title={title}>
      <CircularProgress />
      <Typography variant='bodyMedium'>
        {t('applyAccountAllocations.applying')}
      </Typography>
    </StepStatus>
  );
};
